// The tracker's IndexedDB point store: one autoincrement store of
// { id, point, tripId? } rows. Extracted from tracker_controller.js so the
// promise-wrapping and error-posture choices are documented and unit-tested:
//
//   • add/batch REJECT on failure — losing a point write or a read must surface
//     to the caller (the controller degrades to the in-memory mirror).
//   • delete/clear/count RESOLVE on failure — cleanup and bookkeeping must
//     never break a flush that already succeeded server-side; worst case a row
//     is re-uploaded and the server dedupes on recorded_at.
const DB_NAME = "gr8tracks-trips"
const STORE = "points"

export function openPointDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true })
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function store(db, mode) { return db.transaction(STORE, mode).objectStore(STORE) }

export function idbAdd(db, point, tripId = null) {
  if (!db) return Promise.reject(new Error("IndexedDB unavailable"))
  // Stamp the owning trip on the row so a later trip's flusher never uploads
  // it (schemaless store — no version bump needed, legacy rows stay unstamped).
  return new Promise((res, rej) => { const r = store(db, "readwrite").add(tripId == null ? { point } : { point, tripId: String(tripId) }); r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error) })
}

// Read one batch for the ACTIVE trip only: rows stamped with another trip's id
// are skipped (they can never belong to this upload); unstamped legacy rows
// are treated as the active trip's, matching the pre-stamping behavior.
export function idbBatch(db, limit, tripId = null) {
  if (!db) return Promise.resolve([])
  return new Promise((res, rej) => {
    const rows = []
    const r = store(db, "readonly").openCursor()
    r.onsuccess = () => {
      const cursor = r.result
      if (!cursor || rows.length >= limit) { res(rows); return }
      const value = cursor.value
      if (tripId == null || value.tripId == null || String(value.tripId) === String(tripId)) {
        rows.push({ id: value.id ?? cursor.primaryKey, point: value.point })
      }
      if (rows.length >= limit) { res(rows); return }
      cursor.continue()
    }
    r.onerror = () => rej(r.error)
  })
}

export function idbDelete(db, ids) {
  if (!db) return Promise.resolve()
  return new Promise((res) => {
    try {
      const s = store(db, "readwrite")
      ids.forEach((id) => s.delete(id))
      s.transaction.oncomplete = () => res()
      s.transaction.onerror = () => res()
      s.transaction.onabort = () => res()
    } catch {
      res()
    }
  })
}

export function idbClear(db) {
  if (!db) return Promise.resolve()
  return new Promise((res) => { const r = store(db, "readwrite").clear(); r.onsuccess = () => res(); r.onerror = () => res() })
}

export function idbCount(db) {
  if (!db) return Promise.resolve(0)
  return new Promise((res) => { const r = store(db, "readonly").count(); r.onsuccess = () => res(r.result); r.onerror = () => res(0) })
}
